import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Loader2, BarChart2,
  CalendarDays, CheckCircle2, X, IndianRupee,
  TrendingDown, ReceiptText, Users,
} from 'lucide-react';
import { adminSettlementsAPI } from '../../../services/api';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtINR  = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtShortDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const map = {
    completed:  { bg: C.successSoft, color: C.success, label: 'Completed'  },
    pending:    { bg: C.warnSoft,    color: C.warning, label: 'Pending'    },
    processing: { bg: C.primarySoft, color: C.primary, label: 'Processing' },
    failed:     { bg: C.dangerSoft,  color: C.danger,  label: 'Failed'     },
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted, label: status };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, letterSpacing: .3 }}>{s.label}</span>;
};

const inputStyle = (w) => ({
  height: 38, borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
  padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface, outline: 'none',
  width: w || 'auto',
});

/* ══════════ Confirm Modal ══════════ */
const ConfirmModal = ({ row, onClose, onDone }) => {
  const [form, setForm]       = useState({
    bank_ref:   '',
    utr_number: '',
    remarks:    '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_ref.trim()) return toast.warn('Bank reference is required');
    setSaving(true);
    try {
      await adminSettlementsAPI.confirm({
        user_id:      row.user_id,
        settle_date:  row.settle_date,
        bank_ref:     form.bank_ref.trim(),
        utr_number:   form.utr_number.trim() || null,
        remarks:      form.remarks.trim()    || null,
        gross_amount: row.gross_amount,
        fee_amount:   row.fee_amount,
        gst_amount:   row.gst_amount,
        net_amount:   row.net_amount,
        txn_count:    row.txn_count,
      });
      toast.success('Settlement confirmed successfully');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to confirm settlement');
    } finally {
      setSaving(false);
    }
  };

  const cycleLabel = row.settlement_cycle
    ? row.settlement_cycle.replace(/^T(\d)$/, 'T+$1')
    : row.settlement_cycle;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(20,20,43,.45)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: C.surface, borderRadius: C.radius + 4,
        border: `1px solid ${C.border}`, width: '100%', maxWidth: 540,
        boxShadow: '0 20px 60px rgba(20,20,43,.18)',
      }}>
        {/* modal header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Confirm Settlement</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
              {row.name || row.email} &nbsp;·&nbsp; MID: {row.merchant_id || '—'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.muted }}>
            <X size={18} />
          </button>
        </div>

        {/* summary strip */}
        <div style={{
          margin: '16px 24px', padding: '14px 16px',
          background: C.bg, borderRadius: C.radiusSm, border: `1px solid ${C.borderSoft}`,
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
        }}>
          {[
            { label: 'Settlement Date', value: fmtShortDate(row.settle_date) },
            { label: 'Cycle', value: cycleLabel },
            { label: 'Transactions', value: row.txn_count },
            { label: 'Gross Amount', value: fmtINR(row.gross_amount), color: C.ink },
            { label: `Fee (${row.settlement_percentage}%)`, value: `– ${fmtINR(row.fee_amount)}`, color: C.danger },
            { label: 'Net Payable', value: fmtINR(row.net_amount), color: C.success },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: color || C.body }}>{value}</div>
            </div>
          ))}
        </div>

        {/* form */}
        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>
              Bank Reference <span style={{ color: C.danger }}>*</span>
            </label>
            <input
              value={form.bank_ref}
              onChange={e => set('bank_ref', e.target.value)}
              placeholder="e.g. NEFT/IMPS/UTR reference number"
              style={{ ...inputStyle('100%') }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>
              UTR Number <span style={{ color: C.muted, fontWeight: 400, fontSize: 10 }}>(optional)</span>
            </label>
            <input
              value={form.utr_number}
              onChange={e => set('utr_number', e.target.value)}
              placeholder="e.g. UTR123456789012"
              style={{ ...inputStyle('100%') }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>
              Remarks <span style={{ color: C.muted, fontWeight: 400, fontSize: 10 }}>(optional)</span>
            </label>
            <textarea
              value={form.remarks}
              onChange={e => set('remarks', e.target.value)}
              placeholder="Internal notes about this settlement…"
              rows={2}
              style={{ ...inputStyle('100%'), height: 'auto', padding: '8px 12px', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              type="button" onClick={onClose}
              style={{ height: 38, padding: '0 18px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`, background: C.surface, color: C.body, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              style={{
                height: 38, padding: '0 22px', borderRadius: C.radiusSm,
                background: saving ? C.muted : C.success, color: '#fff',
                border: 'none', fontWeight: 700, fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {saving
                ? <><Loader2 size={13} style={{ animation: 'spin .8s linear infinite' }} /> Confirming…</>
                : <><CheckCircle2 size={14} /> Confirm Settlement</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════ */
const AdminSettlements = () => {
  const [list,     setList]     = useState({ data: [], last_page: 1 });
  const [page,     setPage]     = useState(1);
  const [status,   setStatus]   = useState('');
  const [mid,      setMid]      = useState('');
  const [loading,  setLoading]  = useState(true);

  /* pending today */
  const [pending,        setPending]        = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [confirmRow,     setConfirmRow]     = useState(null);

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const { data } = await adminSettlementsAPI.pendingToday();
      setPending(data?.data || []);
    } catch { /* non-critical */ }
    finally { setLoadingPending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (status) params.status = status;
      if (mid)    params.merchant_id = mid;
      const { data } = await adminSettlementsAPI.list(params);
      setList(data?.data || { data: [], last_page: 1 });
    } catch { toast.error('Failed to load settlements'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPending(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  const totalGross = pending.reduce((s, r) => s + r.gross_amount, 0);
  const totalNet   = pending.reduce((s, r) => s + r.net_amount,   0);
  const totalFee   = pending.reduce((s, r) => s + r.fee_amount,   0);

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Settlements</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Review today's pending settlements and confirm transfers for each merchant.</p>
      </div>

      {/* ── Pending Today Section ── */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, marginBottom: 24, overflow: 'hidden' }}>
        {/* section header */}
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${C.borderSoft}`,
          background: 'linear-gradient(90deg, #f1ebff, #F0FDF4)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: C.primarySoft, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarDays size={16} strokeWidth={1.8} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Today's Pending Settlements</div>
              <div style={{ fontSize: 11, color: C.muted }}>Merchants due for settlement today based on their cycle</div>
            </div>
          </div>

          {/* totals */}
          {!loadingPending && pending.length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { label: 'Merchants', value: pending.length, Icon: Users, iconBg: C.primarySoft, iconColor: C.primary },
                { label: 'Gross', value: fmtINR(totalGross), Icon: IndianRupee, iconBg: C.primarySoft, iconColor: C.primary },
                { label: 'Fee', value: fmtINR(totalFee), Icon: TrendingDown, iconBg: C.dangerSoft, iconColor: C.danger },
                { label: 'Net Total', value: fmtINR(totalNet), Icon: ReceiptText, iconBg: C.successSoft, iconColor: C.success },
              ].map(({ label, value, Icon, iconBg, iconColor }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={13} strokeWidth={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* pending table */}
        {loadingPending ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>
            <Loader2 size={24} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 10, fontSize: 13 }}>Loading pending settlements…</p>
          </div>
        ) : pending.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>
            <CheckCircle2 size={32} style={{ opacity: .3, marginBottom: 10 }} />
            <p style={{ fontSize: 13 }}>No pending settlements for today.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  {['Merchant', 'MID', 'Settle Date', 'Cycle', 'Txns', 'Gross', 'Fee', 'Net Payable', 'Action'].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pending.map((row, i) => {
                  const cycleLabel = row.settlement_cycle
                    ? row.settlement_cycle.replace(/^T(\d)$/, 'T+$1')
                    : row.settlement_cycle;
                  return (
                    <tr key={row.user_id} style={{ borderBottom: `1px solid ${C.borderSoft}`, background: i % 2 === 0 ? C.surface : C.bg }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: C.ink, fontSize: 13 }}>{row.name || '—'}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{row.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, fontFamily: 'Menlo,Consolas,monospace', color: C.primary, fontWeight: 700 }}>{row.merchant_id || '—'}</code>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
                        {fmtShortDate(row.settle_date)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: C.primarySoft, color: C.primary, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 800 }}>
                          {cycleLabel}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>{row.txn_count}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink, whiteSpace: 'nowrap' }}>{fmtINR(row.gross_amount)}</td>
                      <td style={{ padding: '12px 16px', color: C.danger, fontWeight: 600, whiteSpace: 'nowrap' }}>– {fmtINR(row.fee_amount)}</td>
                      <td style={{ padding: '12px 16px', color: C.success, fontWeight: 800, whiteSpace: 'nowrap' }}>{fmtINR(row.net_amount)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button
                          onClick={() => setConfirmRow(row)}
                          style={{
                            height: 32, padding: '0 14px', borderRadius: C.radiusSm,
                            background: C.success, color: '#fff',
                            border: 'none', fontWeight: 700, fontSize: 12,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                          }}
                        >
                          <CheckCircle2 size={12} /> Confirm
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filters — History */}
      <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Quick status pill tabs */}
        {[
          { label: 'All',        val: '',           color: C.primary },
          { label: 'Pending',    val: 'pending',    color: C.warning },
          { label: 'Processing', val: 'processing', color: C.primary },
          { label: 'Completed',  val: 'completed',  color: C.success },
          { label: 'Failed',     val: 'failed',     color: C.danger  },
        ].map(t => (
          <button key={t.val} onClick={() => { setStatus(t.val); setPage(1); }}
            style={{ height: 34, padding: '0 14px', borderRadius: 999, border: `1.5px solid ${status === t.val ? t.color : C.border}`, background: status === t.val ? t.color : 'transparent', color: status === t.val ? '#fff' : C.body, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1, minWidth: 160 }}>
          <input style={{ ...inputStyle('100%'), boxSizing: 'border-box' }} placeholder="Filter by Merchant ID…" value={mid} onChange={e => setMid(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} />
        </div>
        <button onClick={load} style={{ height: 38, padding: '0 16px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Apply</button>
      </div>

      {/* Settlement History Table */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
          </div>
        ) : (list.data || []).length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <BarChart2 size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>No settlements found.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {['Settlement ID', 'Merchant', 'Date', 'Txns', 'Gross', 'Fee', 'Net', 'Status', 'Bank Ref'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((s, i) => (
                    <tr key={s.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, background: i % 2 === 0 ? C.surface : C.bg }}>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, fontFamily: 'Menlo,Consolas,monospace', color: C.primary, fontWeight: 700 }}>{s.settlement_id}</code>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.ink, fontWeight: 600 }}>{s.merchant_id}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>{fmtDate(s.created_at)}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>{s.txn_count}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>{fmtINR(s.gross_amount)}</td>
                      <td style={{ padding: '12px 16px', color: C.danger, fontWeight: 600 }}>{fmtINR(s.fee_amount)}</td>
                      <td style={{ padding: '12px 16px', color: C.success, fontWeight: 700 }}>{fmtINR(s.net_amount)}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={s.status} /></td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>{s.bank_ref || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {list.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '14px 20px', gap: 4, borderTop: `1px solid ${C.borderSoft}` }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: list.last_page }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)} style={{
                    width: 30, height: 30, borderRadius: 6,
                    border: `1px solid ${p === page ? C.primary : C.border}`,
                    background: p === page ? C.primary : C.surface,
                    color: p === page ? '#fff' : C.body,
                    fontWeight: p === page ? 700 : 400, fontSize: 12, cursor: 'pointer',
                  }}>{p}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(list.last_page, p + 1))} disabled={page === list.last_page}
                  style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmRow && (
        <ConfirmModal
          row={confirmRow}
          onClose={() => setConfirmRow(null)}
          onDone={() => { setConfirmRow(null); loadPending(); load(); }}
        />
      )}
    </div>
  );
};

export default AdminSettlements;
